import { PlanMarket, PolicyType } from '@zorro/clients';
import { Card } from '@zorro/design-system/v2/Card';
import { Link } from '@zorro/design-system/v2/Link';
import { Box, Flex, Grid, Stack, Text } from '@zorro/web/design-system';
import { useRoles } from '@zorro/web/ui-utils';

import { useAnalytics } from '../Analytics';
import { CarrierLogoNext } from '../CarrierLogoNext';
import { PlanMarketBadgePageView } from '../PlanMarketBadgePageView';
import { MajorMedicalPlanCardSkeleton } from './MajorMedicalPlanCardSkeleton';

export type MajorMedicalPlanCardProps = {
  carrierName?: string;
  name?: string;
  externalID?: string;
  planMarket?: PlanMarket;
  benefitsSummaryUrl?: string;
  shouldShowRenewalTag?: boolean;
  shouldShowSelfEnrollTag?: boolean;
  policyType?: PolicyType;
  isLoading?: boolean;
  onboardingPeriodId?: string;
};

export const MajorMedicalPlanCard = ({
  carrierName,
  name,
  externalID,
  planMarket,
  benefitsSummaryUrl,
  shouldShowRenewalTag,
  shouldShowSelfEnrollTag,
  policyType,
  isLoading,
  onboardingPeriodId,
}: MajorMedicalPlanCardProps) => {
  const { sendEvent } = useAnalytics();
  const roles = useRoles();

  if (isLoading) {
    return <MajorMedicalPlanCardSkeleton />;
  }

  const planTagText = (() => {
    if (policyType === PolicyType.MEDICARE) return 'Medicare';
    if (policyType === PolicyType.MEDICARE_IFP) return 'Medicare + IFP';
    if (shouldShowRenewalTag) return 'Plan renewal';
    if (shouldShowSelfEnrollTag) return 'Self-enroll';
    return '';
  })();

  return (
    <Card
      variant="white-border"
      style={(theme) => ({
        borderColor: theme.colors.zorroGray[2],
      })}
      pos="relative"
      w="100%"
      p="24px"
    >
      <Grid align="center">
        <Grid.Col span={{ base: 12, md: 3.4 }} pos="relative">
          <Stack h="100%" justify="center">
            <CarrierLogoNext
              plan={{ carrierName: carrierName || '' }}
              width={144}
              height={46}
              style={{ margin: '0' }}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5.6 }}>
          <Stack gap="xs">
            <Text size="xs">{name}</Text>
            <Flex gap="xs" wrap="nowrap" align="center">
              <Text c="zorroGray.4" size="xs" lh="sm">
                Plan ID: {externalID}
              </Text>
              {planMarket && roles.isManagement && (
                <PlanMarketBadgePageView planMarket={planMarket} />
              )}
            </Flex>
          </Stack>
        </Grid.Col>
        {benefitsSummaryUrl && (
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Flex
              justify="end"
              w="100%"
              onClick={() =>
                sendEvent('click_plan_details', {
                  onboarding_period_id: onboardingPeriodId,
                  carrier_name: carrierName,
                  plan_name: name,
                  plan_id: externalID,
                })
              }
            >
              <Link href={benefitsSummaryUrl} target="_blank" variant="primary">
                <Text c="zorroIris.9" fz="xs">
                  View plan details
                </Text>
              </Link>
            </Flex>
          </Grid.Col>
        )}
      </Grid>
      {planTagText && (
        <Box
          pos="absolute"
          bg="zorroGray.8"
          px="md"
          pb="0.125rem"
          right={0}
          top={0}
          style={{ borderBottomLeftRadius: 16 }}
        >
          <Text size="sm">{planTagText}</Text>
        </Box>
      )}
    </Card>
  );
};
